import type {
  InteractionEvent, AiMessage, TimelineEntry,
  LeadStatusValue, LeadStatusHistoryEntry, DealStageValue,
} from '../types';
import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL ?? 'http://localhost:8001/api',
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('crm_token');
  if (token && config.headers) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  // Super admin: auto-inject the globally-selected tenant_id into every request
  // so backend withTenant() / superFilterTenantId() can scope creates & reads correctly.
  const rawUser = localStorage.getItem('crm_user');
  const superTenantId = localStorage.getItem('super_tenant_id');
  if (rawUser && superTenantId) {
    try {
      const u = JSON.parse(rawUser);
      if (u.role === 'super_admin') {
        config.params = { tenant_id: superTenantId, ...config.params };
      }
    } catch {}
  }

  return config;
});

// ---- Auth ----
export const loginApi = (email: string, password: string) =>
  api.post('/login', { email, password }).then(r => r.data);
export const changeMyPassword = (current_password: string, password: string) =>
  api.patch('/me/password', { current_password, password }).then(r => r.data);

// ---- Customers ----
export const fetchCustomers = (params?: object) =>
  api.get('/customers', { params }).then(r => r.data);
export const fetchCustomer = (id: number) =>
  api.get(`/customers/${id}`).then(r => r.data);
export const createCustomer = (data: object) =>
  api.post('/customers', data).then(r => r.data);
export const updateCustomer = (id: number, data: object) =>
  api.patch(`/customers/${id}`, data).then(r => r.data);
export const deleteCustomer = (id: number) =>
  api.delete(`/customers/${id}`).then(r => r.data);
export const importCustomersCsv = (file: File) => {
  const form = new FormData();
  form.append('file', file);
  return api.post('/customers/import/csv', form, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};

// ---- Leads ----
export const fetchLeads = (params?: object) =>
  api.get('/leads', { params }).then(r => r.data);
export const fetchLeadSources = () =>
  api.get('/leads/sources').then(r => r.data as Array<{ source: string; count: number }>);
export const fetchLead = (id: number) =>
  api.get(`/leads/${id}`).then(r => r.data);
export const createLead = (data: object) =>
  api.post('/leads', data).then(r => r.data);
export const updateLead = (id: number, data: object) =>
  api.put(`/leads/${id}`, data).then(r => r.data);
export const convertLead = (id: number, data: object) =>
  api.post(`/leads/${id}/convert`, data).then(r => r.data);
export const deleteLead = (id: number) =>
  api.delete(`/leads/${id}`).then(r => r.data);
export const importLeadsCsv = (file: File) => {
  const form = new FormData();
  form.append('file', file);
  return api.post('/leads/import/csv', form, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};
export const checkLeadDuplicate = (params: { mobile?: string; email?: string; exclude_id?: number }) =>
  api.get('/leads/check-duplicate', { params }).then(r => r.data);
export const fetchLeadInteractions = (leadId: number): Promise<InteractionEvent[]> =>
  api.get(`/leads/${leadId}/interactions`).then(r => r.data);

// ---- Relationship axis (Phase 2, four-axis lead status model) ----
export const fetchRelationshipProposals = (params?: object) =>
  api.get('/leads/relationship-proposals', { params }).then(r => r.data);
export const approveRelationshipProposal = (leadId: number, historyId: number) =>
  api.post(`/leads/${leadId}/relationship/approve`, { history_id: historyId }).then(r => r.data);
export const rejectRelationshipProposal = (leadId: number, historyId: number) =>
  api.post(`/leads/${leadId}/relationship/reject`, { history_id: historyId }).then(r => r.data);
export const bulkApproveRelationshipProposals = (historyIds: number[]) =>
  api.post('/leads/relationship/bulk-approve', { history_ids: historyIds }).then(r => r.data);
export const bulkRejectRelationshipProposals = (historyIds: number[]) =>
  api.post('/leads/relationship/bulk-reject', { history_ids: historyIds }).then(r => r.data);

// ---- Issues axis — product complaints (Phase 3, four-axis lead status model) ----
export const fetchProductComplaints = (params?: object) =>
  api.get('/product-complaints', { params }).then(r => r.data);
export const updateProductComplaint = (id: number, data: object) =>
  api.patch(`/product-complaints/${id}`, data).then(r => r.data);
export const resolveProductComplaint = (id: number, status?: 'resolved' | 'rejected') =>
  api.post(`/product-complaints/${id}/resolve`, status ? { status } : {}).then(r => r.data);

// ---- Organizations ----
export const fetchOrganizations = (params?: object) =>
  api.get('/organizations', { params }).then(r => r.data);
export const fetchOrganization = (id: number) =>
  api.get(`/organizations/${id}`).then(r => r.data);
export const createOrganization = (data: object) =>
  api.post('/organizations', data).then(r => r.data);
export const updateOrganization = (id: number, data: object) =>
  api.put(`/organizations/${id}`, data).then(r => r.data);
export const deleteOrganization = (id: number) =>
  api.delete(`/organizations/${id}`).then(r => r.data);

// ---- Contacts ----
export const fetchContacts = (params?: object) =>
  api.get('/contacts', { params }).then(r => r.data);
export const fetchContact = (id: number) =>
  api.get(`/contacts/${id}`).then(r => r.data);
export const createContact = (data: object) =>
  api.post('/contacts', data).then(r => r.data);
export const updateContact = (id: number, data: object) =>
  api.put(`/contacts/${id}`, data).then(r => r.data);
export const deleteContact = (id: number) =>
  api.delete(`/contacts/${id}`).then(r => r.data);

// ---- Dashboard ----
export const fetchDashboardStats = (params?: object) =>
  api.get('/dashboard/stats', { params }).then(r => r.data);

export const fetchDashboardDrilldown = (
  params: { metric: string; date_from?: string; date_to?: string; agent_id?: string },
) => api.get('/dashboard/drilldown', { params }).then(r => r.data);

// ---- Deals ----
export const fetchDeals = (params?: object) =>
  api.get('/deals', { params }).then(r => r.data);
export const fetchDeal = (id: number) =>
  api.get(`/deals/${id}`).then(r => r.data);
export const createDeal = (data: object) =>
  api.post('/deals', data).then(r => r.data);
export const updateDeal = (id: number, data: object) =>
  api.put(`/deals/${id}`, data).then(r => r.data);
export const deleteDeal = (id: number) =>
  api.delete(`/deals/${id}`).then(r => r.data);

// ---- SIM Calls: bulk convert to leads ----
export const bulkConvertSimCallsToLeads = (simCallIds: number[]) =>
  api.post('/sim-calls/bulk-convert-to-lead', { sim_call_ids: simCallIds }).then(r => r.data);

// ---- Users list (for dropdowns) ----
export const fetchUsersList = () =>
  api.get('/users/list').then(r => r.data);

// ---- Tenants list (slim, for dropdowns) ----
export const fetchTenantsList = () =>
  api.get('/super/tenants', { params: { per_page: 200 } }).then(r => r.data.data as import('../types').Tenant[]);

// ---- Global Tasks ----
// scope='mine' (default) → assigned to or created by current user
// scope='all'            → tenant-wide, admins/managers/super_admin only
// user_id                → optional agent filter (works with scope=all)
export const fetchGlobalTasks = (params?: { scope?: 'mine' | 'all'; user_id?: number; date?: string }) =>
  api.get('/tasks', { params }).then(r => r.data);

// DB-side task counts, independent of the 500-row cap on /tasks. Used by
// the Tasks page sidebar so Overdue / Today / Completed Today stay
// accurate on tenants with large backlogs.
export interface GlobalTaskCounts {
  overdue: number;
  today: number;
  upcoming: number;
  completed_today: number;
  completed_total: number;
  tz: string;
  today_iso: string;
}
export const fetchGlobalTaskCounts = (params?: { scope?: 'mine' | 'all'; user_id?: number }): Promise<GlobalTaskCounts> =>
  api.get('/tasks/counts', { params }).then(r => r.data);

// ---- Notes ----
export const fetchNotes = (type: 'leads' | 'deals' | 'customers' | 'contacts', id: number) =>
  api.get(`/${type}/${id}/notes`).then(r => r.data);
export const createNote = (type: 'leads' | 'deals' | 'customers' | 'contacts', id: number, content: string) =>
  api.post(`/${type}/${id}/notes`, { content }).then(r => r.data);
export const deleteNote = (type: 'leads' | 'deals' | 'customers' | 'contacts', id: number, noteId: number) =>
  api.delete(`/${type}/${id}/notes/${noteId}`).then(r => r.data);

// ---- Tasks (on leads/deals/customers) ----
export const fetchTasks = (type: 'leads' | 'deals' | 'customers' | 'contacts', id: number) =>
  api.get(`/${type}/${id}/tasks`).then(r => r.data);
export const createTask = (type: 'leads' | 'deals' | 'customers' | 'contacts', id: number, data: object) =>
  api.post(`/${type}/${id}/tasks`, data).then(r => r.data);
export const updateTask = (type: 'leads' | 'deals' | 'customers' | 'contacts', id: number, taskId: number, data: object) =>
  api.patch(`/${type}/${id}/tasks/${taskId}`, data).then(r => r.data);
export const deleteTask = (type: 'leads' | 'deals' | 'customers' | 'contacts', id: number, taskId: number) =>
  api.delete(`/${type}/${id}/tasks/${taskId}`).then(r => r.data);

// ---- Activities ----
export const fetchActivities = (type: 'leads' | 'deals' | 'customers' | 'contacts', id: number) =>
  api.get(`/${type}/${id}/activities`).then(r => r.data);

// ---- Search ----
export const globalSearch = (q: string) =>
  api.get('/search', { params: { q } }).then(r => r.data);

// ---- Admin ----
export const fetchUsers = (params?: object) =>
  api.get('/admin/users', { params }).then(r => r.data);
export const createUser = (data: { name: string; email: string; mobile?: string; password: string; role: string; tenant_id?: number }) =>
  api.post('/admin/users', data).then(r => r.data);
export const updateUser = (id: number, data: { name: string; email: string; mobile?: string; role?: string }) =>
  api.patch(`/admin/users/${id}`, data).then(r => r.data);
export const deleteUser = (id: number) =>
  api.delete(`/admin/users/${id}`).then(r => r.data);
export const updateUserRole = (id: number, role: string) =>
  api.patch(`/admin/users/${id}/role`, { role }).then(r => r.data);
export const changeUserPassword = (id: number, password: string) =>
  api.patch(`/admin/users/${id}/password`, { password }).then(r => r.data);

// ---- Role Permissions ----
export const fetchPermissions = (tenantId?: number) =>
  api.get('/admin/permissions', { params: tenantId ? { tenant_id: tenantId } : {} }).then(r => r.data);
export const savePermissions = (data: object, tenantId?: number) =>
  api.put('/admin/permissions', data, { params: tenantId ? { tenant_id: tenantId } : {} }).then(r => r.data);

// ---- Tenants (super admin only) ----
export const fetchTenants = (params?: object) =>
  api.get('/super/tenants', { params }).then(r => r.data);
export const createTenant = (data: object) =>
  api.post('/super/tenants', data).then(r => r.data);
export const fetchTenant = (id: number) =>
  api.get(`/super/tenants/${id}`).then(r => r.data);
export const updateTenant = (id: number, data: object) =>
  api.patch(`/super/tenants/${id}`, data).then(r => r.data);
export const toggleTenantActive = (id: number) =>
  api.patch(`/super/tenants/${id}/toggle`).then(r => r.data);
export const deleteTenant = (id: number) =>
  api.delete(`/super/tenants/${id}`).then(r => r.data);
export const fetchTenantChannels = (id: number) =>
  api.get(`/super/tenants/${id}/channels`).then(r => r.data);
export const upsertTenantChannel = (id: number, channel: string, credentials: object) =>
  api.put(`/super/tenants/${id}/channels/${channel}`, { credentials }).then(r => r.data);
export const fetchTenantWorkflow = (id: number) =>
  api.get(`/super/tenants/${id}/workflow`).then(r => r.data);
export const saveTenantWorkflow = (id: number, data: object) =>
  api.put(`/super/tenants/${id}/workflow`, data).then(r => r.data);
export const fetchSourceIdentities = (id: number) =>
  api.get(`/super/tenants/${id}/source-identities`).then(r => r.data);
export const registerSourceIdentity = (id: number, data: object) =>
  api.post(`/super/tenants/${id}/source-identities`, data).then(r => r.data);
export const revokeSourceIdentity = (tenantId: number, identityId: number) =>
  api.post(`/super/tenants/${tenantId}/source-identities/${identityId}/revoke`).then(r => r.data);

// ---- Devices (Sprint 6) ----
export const fetchDevices = (params?: object) =>
  api.get('/devices', { params }).then(r => r.data);
export const registerDevice = (data: { device_name?: string; phone_imei?: string }) =>
  api.post('/devices/register', data).then(r => r.data);
export const toggleDeviceActive = (id: number) =>
  api.patch(`/devices/${id}/toggle-active`).then(r => r.data);
export const revokeDevice = (id: number) =>
  api.delete(`/devices/${id}`).then(r => r.data);

// ---- SIM Calls (Sprint 6) ----
export const fetchSimCalls = (params?: object) =>
  api.get('/sim-calls', { params }).then(r => r.data);
export const fetchLeadSimCalls = (leadId: number): Promise<import('../types').SimCall[]> =>
  api.get(`/leads/${leadId}/sim-calls`).then(r => r.data);
export const fetchLeadAiMessages = (leadId: number): Promise<AiMessage[]> =>
  api.get(`/leads/${leadId}/ai-messages`).then(r => r.data);
export const fetchLeadTimeline = (leadId: number, channel?: string): Promise<TimelineEntry[]> =>
  api.get(`/leads/${leadId}/timeline`, { params: channel ? { channel } : {} }).then(r => r.data);

export interface SummaryHistoryEntry {
  id: number;
  generated_at: string;
  summary: string;
  sentiment: string | null;
  source_pipeline: string;   // 'skills-simcall-v1' | 'skills-lite-v1' | 'legacy' | ...
  trigger_type: string;      // 'sim_call' | 'whatsapp' | 'scheduler_refresh' | ...
  trigger_ref: Record<string, unknown> | null;
  generation: number;
}
export const fetchLeadSummaryHistory = (leadId: number, limit = 20): Promise<{ data: SummaryHistoryEntry[] }> =>
  api.get(`/leads/${leadId}/summary-history`, { params: { limit } }).then(r => r.data);
export const fetchCustomerSimCalls = (customerId: number): Promise<import('../types').SimCall[]> =>
  api.get(`/customers/${customerId}/sim-calls`).then(r => r.data);
export const fetchSimCallAudio = (id: number): Promise<string> =>
  api.get(`/sim-calls/${id}/audio`, { responseType: 'blob' })
    .then(r => URL.createObjectURL(r.data));

// ---- Campaigns ----
export const fetchCampaigns         = (params?: object) =>
  api.get('/campaigns', { params }).then(r => r.data);
export const fetchCampaign          = (id: number) =>
  api.get(`/campaigns/${id}`).then(r => r.data);
export const createCampaign         = (data: object) =>
  api.post('/campaigns', data).then(r => r.data);
export const updateCampaign         = (id: number, data: object) =>
  api.put(`/campaigns/${id}`, data).then(r => r.data);
export const deleteCampaign         = (id: number) =>
  api.delete(`/campaigns/${id}`).then(r => r.data);
export const launchCampaign         = (id: number) =>
  api.post(`/campaigns/${id}/launch`).then(r => r.data);
export const cancelCampaign         = (id: number) =>
  api.post(`/campaigns/${id}/cancel`).then(r => r.data);
export const fetchCampaignAnalytics = (id: number) =>
  api.get(`/campaigns/${id}/analytics`).then(r => r.data);
export const fetchCampaignDeliveries = (id: number, page = 1, status?: string) =>
  api.get(`/campaigns/${id}/deliveries`, { params: { page, status: status || undefined } }).then(r => r.data);
export const resendDelivery = (campaignId: number, deliveryId: number) =>
  api.post(`/campaigns/${campaignId}/deliveries/${deliveryId}/resend`).then(r => r.data);

// ---- Campaign Segments ----
export const fetchSegments  = () =>
  api.get('/campaign-segments').then(r => r.data);
export const fetchSegment   = (id: number) =>
  api.get(`/campaign-segments/${id}`).then(r => r.data);
export const createSegment  = (data: object) =>
  api.post('/campaign-segments', data).then(r => r.data);
export const updateSegment  = (id: number, data: object) =>
  api.put(`/campaign-segments/${id}`, data).then(r => r.data);
export const deleteSegment  = (id: number) =>
  api.delete(`/campaign-segments/${id}`).then(r => r.data);
export const previewSegment = (filters: object, entityType: string, channel: string) =>
  api.post('/campaign-segments/preview', { filters, entity_type: entityType, channel }).then(r => r.data);

// ---- WA Templates ----
export const fetchWaTemplates  = () =>
  api.get('/wa-templates').then(r => r.data);
export const createWaTemplate  = (data: object) =>
  api.post('/wa-templates', data).then(r => r.data);
export const updateWaTemplate  = (id: number, data: object) =>
  api.put(`/wa-templates/${id}`, data).then(r => r.data);
export const deleteWaTemplate  = (id: number) =>
  api.delete(`/wa-templates/${id}`).then(r => r.data);

// ---- Live Monitor ----
export const fetchLiveMonitor = () =>
  api.get('/live-monitor').then(r => r.data);

// ---- Call Reports ----
export const fetchCallSummary       = (params?: object) =>
  api.get('/reports/calls/summary',        { params }).then(r => r.data);
export const fetchCallPerformance   = (params?: object) =>
  api.get('/reports/calls/performance',    { params }).then(r => r.data);
export const fetchCallDaywise       = (params?: object) =>
  api.get('/reports/calls/daywise',        { params }).then(r => r.data);
export const fetchCallHourly        = (params?: object) =>
  api.get('/reports/calls/hourly',         { params }).then(r => r.data);
export const fetchCallNeverAttended = (params?: object) =>
  api.get('/reports/calls/never-attended', { params }).then(r => r.data);
export const fetchCallHistory       = (params?: object) =>
  api.get('/reports/calls/history',        { params }).then(r => r.data);
export const fetchCallAgents        = () =>
  api.get('/reports/calls/agents').then(r => r.data);

// ---- Lead Deal Stage (Phase 4, four-axis lead status model, 2026-09-20) ----
export const patchLeadStatus = (
  leadId: number,
  payload: { deal_stage: DealStageValue; lost_reason?: string | null; remark?: string },
) =>
  api.patch(`/leads/${leadId}/status`, payload).then(r => r.data);

export const fetchLeadStatusHistory = (leadId: number): Promise<LeadStatusHistoryEntry[]> =>
  api.get(`/leads/${leadId}/status/history`).then(r => r.data);

// ---- Lead Assignment ----
export const assignLead      = (leadId: number, userId: number) =>
  api.post(`/leads/${leadId}/assign`, { user_id: userId }).then(r => r.data);
export const reassignLead    = (leadId: number, userId: number) =>
  api.post(`/leads/${leadId}/reassign`, { user_id: userId }).then(r => r.data);
export const autoAssignLead  = (leadId: number) =>
  api.post('/assignment/auto-assign', { lead_id: leadId }).then(r => r.data);
export const fetchUnassignedLeads = (page = 1) =>
  api.get('/assignment/unassigned', { params: { page } }).then(r => r.data);

// ---- Assignment Settings ----
export const fetchAssignmentSettings = () =>
  api.get('/assignment/settings').then(r => r.data);
export const updateAssignmentSettings = (data: object) =>
  api.put('/assignment/settings', data).then(r => r.data);

// ---- Agent Assignment Config ----
export const fetchAgentsWithConfig = () =>
  api.get('/assignment/agents').then(r => r.data);
export const fetchAgentConfig = (agentId: number) =>
  api.get(`/assignment/agents/${agentId}/config`).then(r => r.data);
export const updateAgentConfig = (agentId: number, data: object) =>
  api.put(`/assignment/agents/${agentId}/config`, data).then(r => r.data);

// ---- Attendance ----
export const checkIn          = () =>
  api.post('/attendance/check-in').then(r => r.data);
export const checkOut         = () =>
  api.post('/attendance/check-out').then(r => r.data);
export const fetchTodayAttendance = () =>
  api.get('/attendance/today').then(r => r.data);
export const markAttendance   = (userId: number, status: string, notes?: string) =>
  api.post('/attendance/mark', { user_id: userId, status, notes }).then(r => r.data);
export const setMyAvailability = (status: string) =>
  api.patch('/attendance/availability', { status }).then(r => r.data);
export const fetchAttendanceHistory = (from: string, to: string, userId?: number) =>
  api.get('/attendance/history', { params: { from, to, user_id: userId } }).then(r => r.data);

// ---- Super Admin: Analyzer Console ----
export const fetchAnalyzerHealth = (tenantId?: number) =>
  api.get('/super/analyzer/health', { params: tenantId ? { tenant: tenantId } : {} }).then(r => r.data);
export const reanalyzeLead = (leadId: number, apply = false) =>
  api.post('/super/analyzer/reanalyze', { lead_id: leadId, apply }).then(r => r.data);
export const markTenantLeadsDirty = (tenantId: number, onlySummarized = true) =>
  api.post('/super/analyzer/mark-dirty', { tenant_id: tenantId, only_summarized: onlySummarized }).then(r => r.data);

// ---- Super Admin: Per-tenant AI usage (2026-10-23) ----
export type AiUsageRow = {
  tenant_id: number;
  name: string;
  slug: string;
  analyzer:      { runs: number; prompt_tokens: number; completion_tokens: number; cost_usd: number };
  transcription: { calls: number; audio_minutes: number; cost_usd: number };
  total_cost_usd: number;
};
export type AiUsageResponse = {
  from: string;
  to: string;
  tenants: AiUsageRow[];
  totals: {
    analyzer_cost_usd: number;
    transcription_cost_usd: number;
    total_cost_usd: number;
    analyzer_runs: number;
    transcription_calls: number;
    prompt_tokens: number;
    completion_tokens: number;
    audio_minutes: number;
  };
};
export const fetchAiUsage = (params?: { from?: string; to?: string }) =>
  api.get('/super/ai-usage', { params }).then(r => r.data as AiUsageResponse);

// ---- Super Admin: Per-tenant toggles (2026-08-31; extended 2026-10-23) ----
export const fetchTenantEmailToggles = () =>
  api.get('/super/tenant-email/toggles').then(r => r.data);
export const updateTenantEmailToggles = (tenantId: number, payload: {
  reports_email_enabled?: boolean;
  cost_alerts_email_enabled?: boolean;
  transcription_enabled?: boolean;
  analyzer_enabled?: boolean;
}) => api.patch(`/super/tenant-email/toggles/${tenantId}`, payload).then(r => r.data);

// ---- Super Admin: A/B Review Console (PR6 of timeline refactor) ----
export const fetchAbReviewSamples = (tenantId: number, perStratum = 5) =>
  api.get('/super/ab-review/samples', { params: { tenant: tenantId, per_stratum: perStratum } }).then(r => r.data);
export const fetchAbReviewSample = (shadowRunId: number) =>
  api.get(`/super/ab-review/sample/${shadowRunId}`).then(r => r.data);
export const submitAbReviewVerdict = (payload: {
  shadow_run_id: number;
  verdict: 'new_better' | 'legacy_better' | 'both_equivalent' | 'both_bad';
  stratum?: string | null;
  notes?: string | null;
}) => api.post('/super/ab-review/verdict', payload).then(r => r.data);
export const fetchAbReviewStats = (tenantId: number) =>
  api.get('/super/ab-review/stats', { params: { tenant: tenantId } }).then(r => r.data);

// ---- Super Admin: Teleobi WhatsApp Config ----
export const fetchTeleobiConfig = (tenantId: number) =>
  api.get('/super/teleobi/configs', { params: { tenant: tenantId } }).then(r => r.data);
export const saveTeleobiConfig = (data: object) =>
  api.put('/super/teleobi/configs', data).then(r => r.data);
export const deleteTeleobiConfig = (tenantId: number) =>
  api.delete('/super/teleobi/configs', { params: { tenant: tenantId } }).then(r => r.data);
export const dispatchTeleobiSend = (leadId: number) =>
  api.post('/super/teleobi/dispatch', { lead_id: leadId }).then(r => r.data);

// ---- Super Admin: Agent WhatsApp (chrome extension sync) ----
// Public endpoint; agent_id scopes the chats to a single device install.
// Testing-phase; see pullChats in WhatsAppWebSyncController.
export type AgentWhatsAppMessage = {
  message_id: string;
  text: string;
  timestamp: string;
  // Server may send any of these spellings — the component normalizes.
  direction: 'incoming' | 'outgoing' | 'inbound' | 'outbound' | 'in' | 'out' | 'sent' | string;
  sender?: string;
  // Optional WA-style boolean markers that some scrapers emit.
  from_me?: boolean;
  fromMe?: boolean;
  is_from_me?: boolean;
};
export type AgentWhatsAppChat = {
  chat_id: string;
  customer_name: string | null;
  customer_phone: string | null;
  last_message: AgentWhatsAppMessage | null;
  unread_count: number;
  messages: AgentWhatsAppMessage[];
};
export const fetchAgentWhatsAppChats = (agentId: number) =>
  api.get('/whatsapp-web/chats', { params: { agent_id: agentId } })
    .then(r => r.data as { success: boolean; agent_id: number; chats: AgentWhatsAppChat[] });

export default api;
